import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CounterpartyListRow } from '@/types/counterparty.types';
import { AgreementStatus } from '@/types/trades.types';
import { clsx } from 'clsx';
import { CheckCircleIcon, ClockIcon, XCircleIcon, StopCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
// LR-685: the desk's date convention is ISO `YYYY-MM-DD` (LR-667), and the shared
// formatter reads a bare backend date from its literal Y-M-D. The local helper this
// replaces built `M/D/YYYY` out of LOCAL getters, so it was both the wrong format and
// a day off for viewers west of UTC (a date-only string parses as UTC midnight).
import { formatDate } from '@/utils/format';
import { formatMta } from '@/utils/trades/mta-display';

export const createCounterpartyColumns = (
  onEdit?: (cp: CounterpartyListRow) => void,
): ColumnDef<CounterpartyListRow>[] => [
  {
    accessorKey: 'name',
    header: 'Counterparty Name',
    size: 250,
    cell: (info) => {
      const parentName = info.row.original.parentName;
      return (
        <div className="flex flex-col overflow-hidden">
          <span className="font-medium text-gray-900 truncate">{info.getValue() as string}</span>
          {parentName && (
            <span className="text-xs text-gray-500 truncate">Same legal entity as {parentName}</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'clientId',
    header: 'Counterparty ID',
    size: 180,
    cell: (info) => <span className="text-gray-500 font-mono text-xs">{info.getValue() as string}</span>,
  },
  {
    accessorKey: 'creditRating',
    header: 'Rating',
    size: 100,
    cell: (info) => (
      <span className="text-xs font-semibold text-gray-700">
        {(info.getValue() as string) || '—'}
      </span>
    ),
  },
  {
    // The raw column, not the `legalEntity` alias (`country || 'International'`):
    // search is server-side now, and it can never match that placeholder (LR-661).
    accessorKey: 'country',
    header: 'Country',
    size: 150,
    cell: (info) => (info.getValue() as string) || <span className="text-gray-400">—</span>,
  },
  {
    id: 'minimumTransferAmount',
    header: 'Min Transfer Amount',
    size: 170,
    cell: (info) => {
      const row = info.row.original;
      if (row.minimumTransferAmount == null) {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span className="text-xs font-mono tabular-nums text-gray-700">
          {formatMta(row.minimumTransferAmount, row.minimumTransferAmountCurrency)}
        </span>
      );
    },
  },
  {
    id: 'agreements',
    header: 'Agreements',
    size: 160,
    cell: (info) => (
      <div className="flex flex-wrap gap-1">
        {info.row.original.agreements.map((agreement) => (
          <span
            key={agreement.id}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800"
          >
            {agreement.type.toUpperCase()} · {agreement.currency}
          </span>
        ))}
      </div>
    ),
  },
  /* Hidden: Agreement ID as not in API */

  ...((false as boolean) ? [{
    accessorKey: 'primaryAgreement.agreementId',
    header: 'Agreement ID',
    size: 150,
  }] : []),
  {
    id: 'validPeriod',
    header: 'Valid Period',
    size: 200,
    cell: (info) => (
      <div className="flex flex-col">
        {info.row.original.agreements.map((agr) => (
          <span key={agr.id} className="text-xs text-gray-700">
            {info.row.original.agreements.length > 1 && `${agr.type.toUpperCase()}: `}
            {formatDate(agr.validFrom)} — {agr.isPerpetual ? 'Perpetual' : formatDate(agr.validTo ?? null)}
          </span>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'daysRemaining',
    header: 'Remaining',
    size: 100,
    cell: (info) => {
      const days = info.getValue() as number | null;
      if (days === null) return <span className="text-gray-400">—</span>;
      const color = days < 0 ? 'text-red-600' : days < 30 ? 'text-orange-600' : days < 90 ? 'text-yellow-600' : 'text-gray-600';
      return <span className={clsx('font-medium text-xs', color)}>{days}d</span>;
    },
  },
  {
    accessorKey: 'isActive',
    header: 'Status',
    size: 100,
    cell: (info) => {
      const isActive = info.getValue() as boolean;
      return (
        <span className={clsx(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        )}>
          {isActive ? (
            <CheckCircleIcon className="h-3 w-3 mr-1" />
          ) : (
            <StopCircleIcon className="h-3 w-3 mr-1" />
          )}
          {isActive ? 'Active' : 'Inactive'}
        </span>
      );
    },
  },
  /* Hidden: Multi-status badge as not fully supported by API enum */

  ...((false as boolean) ? [{
    accessorKey: 'statusBadge',
    header: 'Status (Detailed)',
    size: 140,
    cell: (info: { getValue: () => unknown }) => {
      const status = info.getValue() as AgreementStatus;

      const config: Record<AgreementStatus, { bg: string; text: string; icon: React.ComponentType<{ className?: string }> }> = {
        ACTIVE: {
          bg: 'bg-green-100/80',
          text: 'text-green-800',
          icon: CheckCircleIcon,
        },
        PENDING_APPROVAL: {
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          icon: ClockIcon,
        },
        EXPIRED: {
          bg: 'bg-red-50',
          text: 'text-red-700',
          icon: XCircleIcon,
        },
        TERMINATED: {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          icon: StopCircleIcon,
        },
      };

      const { bg, text, icon: Icon } = config[status] || config.TERMINATED;

      return (
        <span className={clsx(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          bg,
          text
        )}>
          <Icon className="h-3 w-3 stroke-[2] mr-1" />
          {status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ')}
        </span>
      );
    },
  }] : []),
  // LR-584: the Edit column only renders when the caller passes onEdit — the
  // page omits it for users without the manage_registries capability.
  ...(onEdit ? [{
    id: 'edit',
    header: 'Edit',
    size: 120,
    cell: (info) => (
      <button
        type="button"
        aria-label={`Edit ${info.row.original.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(info.row.original);
        }}
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors border border-blue-200"
      >
        <PencilSquareIcon className="h-3 w-3 mr-1" />
        Edit
      </button>
    ),
  } satisfies ColumnDef<CounterpartyListRow>] : []),
];
